<template>
    <div>
        <template v-if="items.length">
            <!-- Antragsteller/Versicherungsnehmer -->
            <div class="mb-4">
                <div class="font-xl-links-bold mb-3">
                    Antragsteller/Versicherungsnehmer
                </div>
                <div class="font-xl-links">
                    <template v-if="hasVN">
                        <template v-if="vnPerson.vorname && vnPerson.nachname">
                            {{ vnPerson.vorname }} {{ vnPerson.nachname }}
                            <template v-if="vnPerson.geburtstag && isValidGeburtstag(vnPerson.geburtstag)">
                                (geb. {{ formatiertDatum(vnPerson.geburtstag) }})
                            </template>
                            <template v-else>
                                (bitte das Geburtsdatum angeben)
                            </template>
                        </template>
                        <template v-else>
                            Bitte ergänzen Sie den Namen
                        </template>
                    </template>
                    <template v-else>
                        Im folgenden Schritt ergänzen Sie die Angaben zum Antragsteller/Versicherungsnehmer.
                    </template>
                </div>
            </div>

            <!-- ==================== ARNS: Versicherte Person(en) ==================== -->
            <template v-if="isARNS">
                <div class="font-xl-links-bold mb-3">
                    {{ items.length === 1 ? 'Versicherte Person' : 'Versicherte Personen' }}
                </div>

                <div v-for="(item, index) in items" :key="item.id" :class="{ 'mb-3': index !== items.length - 1 }">
                    <div class="font-xl-links">
                        <template v-if="item.vorname && item.nachname">
                            {{ item.vorname }} {{ item.nachname }}
                            <template v-if="item.geburtstag && isValidGeburtstag(item.geburtstag)">
                                (geb. {{ formatiertDatum(item.geburtstag) }})
                            </template>
                            <template v-else>
                                (bitte das Geburtsdatum angeben)
                            </template>
                        </template>
                        <template v-else>
                            {{ index + 1 }}. Person: Bitte ergänzen Sie den Namen
                        </template>
                    </div>
                    
                    <!-- ARNS Beiträge pro Person -->
                    <div class="row mt-3" v-if="item.tarif7 === 1">
                        <div class="col-12 col-md-6 font-xl-links">
                            Ausland Einzel (ARNS)
                        </div>
                        <div v-if="!isValidGeburtstag(item.geburtstag)" class="col-12 col-md-6 d-flex flex-column align-items-md-end text-muted">
                            Bitte geben Sie das Geburtsdatum ein.
                        </div>
                        <div v-else class="col-12 col-md-6 d-flex flex-column align-items-md-end">
                            <div class="beitrag-zeile">
                                <span class="beitrag-label">Erstbeitrag ({{ getMindestlaufzeitText(item) }}):</span>
                                <strong class="beitrag-wert">{{ getErstbeitragStr(item, 'tarif7') }}</strong>
                            </div>
                            <div class="beitrag-zeile">
                                <span class="beitrag-label">Jahresbeitrag (ab {{ getJahresbeitragStartStr() }}):</span>
                                <strong class="beitrag-wert">{{ getJahresbeitragStr(item, 'tarif7') }}</strong>
                            </div>
                        </div>
                    </div>
                    <hr v-if="index !== items.length - 1">
                </div>
            </template>

            <!-- ==================== ARNF: Familienversicherung ==================== -->
            <template v-if="isARNF">
                <div class="font-xl-links-bold mb-3">
                    Familienversicherung
                </div>

                <div class="font-xl-links mb-2">
                    <template v-if="vnPerson && vnPerson.vorname && vnPerson.nachname">
                        {{ vnPerson.vorname }} {{ vnPerson.nachname }}
                        <template v-if="vnPerson.geburtstag && isValidGeburtstag(vnPerson.geburtstag)">
                            (geb. {{ formatiertDatum(vnPerson.geburtstag) }})
                        </template>
                        <template v-else>
                            (bitte das Geburtsdatum angeben)
                        </template>
                    </template>
                    <template v-else-if="items[0]?.vorname && items[0]?.nachname">
                        {{ items[0].vorname }} {{ items[0].nachname }}
                        <template v-if="items[0].geburtstag && isValidGeburtstag(items[0].geburtstag)">
                            (geb. {{ formatiertDatum(items[0].geburtstag) }})
                        </template>
                        <template v-else>
                            (bitte das Geburtsdatum angeben)
                        </template>
                    </template>
                    <template v-else>
                        Bitte ergänzen Sie den Namen
                    </template>
                </div>

                <!-- Geburtsdatum der ältesten Person -->
                <div class="font-xl-links mb-3">
                    <template v-if="props.geburtsdatumAeltestePerson && isValidGeburtstag(props.geburtsdatumAeltestePerson)">
                        Geburtsdatum der ältesten Person: {{ formatiertDatum(props.geburtsdatumAeltestePerson) }}
                    </template>
                    <template v-else>
                        Geburtsdatum der ältesten Person: bitte angeben
                    </template>
                </div>
                
                <!-- ARNF Beiträge -->
                <div class="row mt-3" v-if="items[0]?.tarif8 === 1">
                    <div class="col-12 col-md-6 font-xl-links">
                        Ausland Familie (ARNF)
                    </div>
                    <div v-if="!isValidGeburtstag(props.geburtsdatumAeltestePerson)" class="col-12 col-md-6 d-flex flex-column align-items-md-end text-muted">
                        Bitte geben Sie das Geburtsdatum der ältesten Person ein.
                    </div>
                    <div v-else class="col-12 col-md-6 d-flex flex-column align-items-md-end">
                        <div class="beitrag-zeile">
                            <span class="beitrag-label">Erstbeitrag ({{ getMindestlaufzeitTextARNF() }}):</span>
                            <strong class="beitrag-wert">{{ getErstbeitragStrARNF() }}</strong>
                        </div>
                        <div class="beitrag-zeile">
                            <span class="beitrag-label">Jahresbeitrag (ab {{ getJahresbeitragStartStr() }}):</span>
                            <strong class="beitrag-wert">{{ getJahresbeitragStrARNF() }}</strong>
                        </div>
                    </div>
                </div>
            </template>
            
            <!-- Horizontaler Trenner mit Abstand vor Gesamtbeitrag -->
            <hr class="mt-4">
            
            <!-- Gesamtbeitrag -->
            <div class="row mb-2">
                <div class="col-12 col-md-6 d-flex align-items-center">
                    <div class="font-xl-links-bold">Gesamt Erstbeitrag</div> 
                    <div class="ms-2"><MindestvertragsdauerPopover /></div>
                </div>
                <div class="col-12 col-md-6 font-xl-rechts-bold" :class="{ 'text-muted': !alleGeburtsdatenGueltig }">
                    {{ gesamtErstbeitragStr }}
                </div>
            </div>
            
            <!-- Hinweis zur automatischen Verlängerung (nur wenn alle Daten gültig) -->
            <div v-if="alleGeburtsdatenGueltig" class="mt-3 font-base-links text-muted">
                * bei automatischer Verlängerung um 12 Monate beträgt der Folgebeitrag in Summe {{ gesamtJahresbeitragStr }}
            </div>
        </template>
        <template v-else>
            <div class="font-xl-links text-center py-4">
                Bitte fügen Sie zu versichernde Personen hinzu.
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { 
    formatiertDatum, 
    isValidDateInPast,
    berechneMindestlaufzeit,
    berechneJahresbeitragStart,
    berechneAR20Beitraege,
    formatiereBeitrag
} from '@/helpers/hilfsfunktionen';
import MindestvertragsdauerPopover from '@/components/arn/info_mindestvertragsdauer_popover.vue';

const props = defineProps({
    items: {
        type: Array,
        default: () => []
    },
    vndaten: {
        type: Object,
        default: () => ({ id: 'initial' })
    },
    versicherungsbeginn: {
        type: String,
        default: ''
    },
    geburtsdatumAeltestePerson: {
        type: String,
        default: ''
    }
});

// Tarif-Weiche: ARNS (tarif7) oder ARNF (tarif8)
const isARNS = computed(() => {
    const firstItem = props.items[0];
    return firstItem && firstItem.tarif7 === 1;
});

const isARNF = computed(() => {
    const firstItem = props.items[0];
    return firstItem && firstItem.tarif8 === 1;
});

// Prüft, ob eine Person als VN ausgewählt wurde
const hasVN = computed(() => {
    if (props.vndaten.id === 'initial') return false;
    // Bei abweichendem VN (vn0): nur true wenn Name bereits eingegeben
    if (props.vndaten.id === 'vn0') {
        return !!(props.vndaten.vorname && props.vndaten.nachname);
    }
    return true;
});

// Findet die VN-Person in den Items (falls ausgewählt)
const vnPerson = computed(() => {
    if (!hasVN.value) {
        return null;
    }
    return props.items.find(item => item.id === props.vndaten.id) || props.vndaten;
});

const isValidGeburtstag = (datum) => {
    return datum && isValidDateInPast(datum);
};

// Mindestlaufzeit-Text für eine Person
const getMindestlaufzeitText = (item) => {
    const ml = berechneMindestlaufzeit(props.versicherungsbeginn);
    if (ml.monate > 0) {
        return `${ml.vonStr} - ${ml.bisStr}`;
    }
    return '';
};

// Jahresbeitrag-Start für alle (gleich)
const getJahresbeitragStartStr = () => {
    return berechneJahresbeitragStart(props.versicherungsbeginn);
};

// Erstbeitrag für eine Person (tarif7 = ARNS, tarif8 = ARNF)
const getErstbeitragStr = (item, tarifKey) => {
    const beitragKey = `${tarifKey}beitrag`;
    if (!item[beitragKey] || item[beitragKey] <= 0) {
        return '- €';
    }
    const beitraege = berechneAR20Beitraege(item[beitragKey], props.versicherungsbeginn);
    return formatiereBeitrag(beitraege.erstbeitrag);
};

// Jahresbeitrag für eine Person
const getJahresbeitragStr = (item, tarifKey) => {
    const beitragKey = `${tarifKey}beitrag`;
    if (!item[beitragKey] || item[beitragKey] <= 0) {
        return '- €';
    }
    return formatiereBeitrag(item[beitragKey]);
};

// ARNF-spezifische Funktionen (Berechnung basiert auf ältester Person)
const getMindestlaufzeitTextARNF = () => {
    const ml = berechneMindestlaufzeit(props.versicherungsbeginn);
    if (ml.monate > 0) {
        return `${ml.vonStr} - ${ml.bisStr}`;
    }
    return '';
};

const getErstbeitragStrARNF = () => {
    const firstItem = props.items[0];
    if (!firstItem || !firstItem.tarif8beitrag || firstItem.tarif8beitrag <= 0) {
        return '- €';
    }
    const beitraege = berechneAR20Beitraege(firstItem.tarif8beitrag, props.versicherungsbeginn);
    return formatiereBeitrag(beitraege.erstbeitrag);
};

const getJahresbeitragStrARNF = () => {
    const firstItem = props.items[0];
    if (!firstItem || !firstItem.tarif8beitrag || firstItem.tarif8beitrag <= 0) {
        return '- €';
    }
    return formatiereBeitrag(firstItem.tarif8beitrag);
};

// Prüft ob alle Personen ein gültiges Geburtsdatum haben
const alleGeburtsdatenGueltig = computed(() => {
    // Bei ARNF: Prüfe Geburtsdatum der ältesten Person statt der Personen-Geburtsdaten
    if (isARNF.value) {
        return isValidGeburtstag(props.geburtsdatumAeltestePerson);
    }
    // Bei ARNS: Prüfe alle Personen
    return props.items.every(item => {
        const hatARNTarif = item.tarif7 === 1 || item.tarif8 === 1;
        return !hatARNTarif || isValidGeburtstag(item.geburtstag);
    });
});

// Gesamt-Erstbeitrag (nur für Personen mit gültigem Geburtsdatum)
const gesamtErstbeitrag = computed(() => {
    let summe = 0;
    
    // ARNF: Berechnung basiert auf Geburtsdatum der ältesten Person
    if (isARNF.value) {
        if (isValidGeburtstag(props.geburtsdatumAeltestePerson)) {
            const firstItem = props.items[0];
            if (firstItem && firstItem.tarif8 === 1 && firstItem.tarif8beitrag > 0) {
                const beitraege = berechneAR20Beitraege(firstItem.tarif8beitrag, props.versicherungsbeginn);
                summe += beitraege.erstbeitrag;
            }
        }
        return summe;
    }
    
    // ARNS: Berechnung basiert auf Geburtsdatum jeder Person
    props.items.forEach(item => {
        if (isValidGeburtstag(item.geburtstag)) {
            if (item.tarif7 === 1 && item.tarif7beitrag > 0) {
                const beitraege = berechneAR20Beitraege(item.tarif7beitrag, props.versicherungsbeginn);
                summe += beitraege.erstbeitrag;
            }
        }
    });
    return summe;
});

const gesamtErstbeitragStr = computed(() => {
    if (!alleGeburtsdatenGueltig.value) {
        return 'Bitte alle Geburtsdaten eingeben';
    }
    return formatiereBeitrag(gesamtErstbeitrag.value);
});

// Gesamt-Jahresbeitrag (Folgebeitrag, nur für Personen mit gültigem Geburtsdatum)
const gesamtJahresbeitrag = computed(() => {
    let summe = 0;
    
    // ARNF: Berechnung basiert auf Geburtsdatum der ältesten Person
    if (isARNF.value) {
        if (isValidGeburtstag(props.geburtsdatumAeltestePerson)) {
            const firstItem = props.items[0];
            if (firstItem && firstItem.tarif8 === 1 && firstItem.tarif8beitrag > 0) {
                summe += firstItem.tarif8beitrag;
            }
        }
        return summe;
    }
    
    // ARNS: Berechnung basiert auf Geburtsdatum jeder Person
    props.items.forEach(item => {
        if (isValidGeburtstag(item.geburtstag)) {
            if (item.tarif7 === 1 && item.tarif7beitrag > 0) {
                summe += item.tarif7beitrag;
            }
        }
    });
    return summe;
});

const gesamtJahresbeitragStr = computed(() => {
    if (!alleGeburtsdatenGueltig.value) {
        return '- €';
    }
    return formatiereBeitrag(gesamtJahresbeitrag.value);
});
</script>

<style scoped>
.font-xl-links-bold {
    font-weight: 700;
}

.font-xl-rechts-bold {
    font-weight: 700;
    text-align: right;
}

@media (max-width: 767px) {
    .font-xl-rechts-bold {
        text-align: left;
    }
}

.beitrag-zeile {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 0.25rem;
}

.beitrag-label {
    font-size: 0.9rem;
}

.beitrag-wert {
    font-size: 1rem;
}

@media (min-width: 768px) {
    .beitrag-zeile {
        flex-direction: row;
        gap: 0.5rem;
        margin-bottom: 0;
        align-items: flex-end;
    }
    
    .beitrag-label {
        font-size: 1rem;
    }
}
</style>
